import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Target, Layers, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import LoadingBrief from "@/components/LoadingBrief";
import { generateBrief } from "@/lib/api";

const SAMPLE_CHIPS = [
  { label: "Stripe", target: "Stripe", user: "" },
  { label: "Plaid", target: "Plaid", user: "" },
  { label: "Ramp vs Brex", target: "Ramp", user: "Brex" },
  { label: "Wise vs Revolut", target: "Wise", user: "Revolut" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [target, setTarget] = useState("");
  const [userProduct, setUserProduct] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (e) => {
    e?.preventDefault?.();
    const t = target.trim();
    if (!t) {
      toast.error("Enter a company or product name to analyse.");
      return;
    }
    setLoading(true);
    try {
      const brief = await generateBrief({
        target_company: t,
        user_product: userProduct.trim() || null,
      });
      navigate(`/brief/${brief.id}`, { state: { brief } });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to generate brief";
      toast.error(String(msg));
      setLoading(false);
    }
  };

  const applySample = (s) => {
    setTarget(s.target);
    setUserProduct(s.user);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <SiteHeader />
        <LoadingBrief target={target} />
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-electric opacity-[0.06] blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-navy opacity-[0.04] blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 lg:pt-28 lg:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-mono tracking-wider uppercase text-slate-600 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-electric pulse-dot" />
              AI Competitive Intelligence · Fintech
            </div>

            <h1
              data-testid="landing-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-navy"
            >
              Competitive intelligence briefs for fintech PMs,
              <span className="text-electric"> in 60 seconds.</span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Real-time AI analysis of product positioning, feature gaps, pricing models, and
              strategic opportunities — synthesised into one shareable brief.
            </p>
          </motion.div>

          {/* INPUT CARD */}
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            onSubmit={handleGenerate}
            className="mt-10 rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-30px_rgba(11,37,69,0.25)] p-6 sm:p-8 max-w-3xl"
            data-testid="generate-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-slate-500 mb-2">
                  Target Company or Product *
                </label>
                <Input
                  data-testid="company-input"
                  autoFocus
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="e.g. Stripe, Plaid, Ramp, Wise…"
                  className="h-12 text-base border-slate-300 focus-visible:ring-2 focus-visible:ring-electric focus-visible:border-electric"
                />
              </div>
              <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-slate-500 mb-2">
                  Your Product <span className="text-slate-400 normal-case tracking-normal font-sans">(optional)</span>
                </label>
                <Input
                  data-testid="comparison-product-input"
                  value={userProduct}
                  onChange={(e) => setUserProduct(e.target.value)}
                  placeholder="e.g. Mercury, Adyen, Brex…"
                  className="h-12 text-base border-slate-300 focus-visible:ring-2 focus-visible:ring-electric focus-visible:border-electric"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button
                type="submit"
                data-testid="generate-brief-button"
                className="h-12 px-6 bg-electric text-white hover:bg-[#1448D8] rounded-lg font-semibold text-base shadow-lg shadow-electric/25 group"
              >
                Generate Brief
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-mono tracking-widest uppercase text-slate-400 pr-1">Try:</span>
                {SAMPLE_CHIPS.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => applySample(s)}
                    data-testid={`sample-chip-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-electric hover:text-electric transition-colors"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.form>

          {/* Value props */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
            {[
              { icon: Zap, title: "60-second synthesis", copy: "From a company name to a one-page brief before your coffee cools." },
              { icon: Target, title: "PM-shaped output", copy: "Positioning map, feature gaps, opportunities, and prioritised next steps." },
              { icon: Layers, title: "Fintech-native lens", copy: "Take rates, interchange, KYC/KYB, and market-segment nuance built in." },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25 + i * 0.08 }}
                className="rounded-xl border border-slate-200 bg-white p-5 card-lift"
              >
                <f.icon className="h-5 w-5 text-electric" />
                <h3 className="mt-3 font-display text-base font-bold text-navy">{f.title}</h3>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{f.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample brief teaser */}
      <section className="border-t border-slate-100 bg-slate-50/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-slate-500 mb-4">
            <Sparkles className="h-3.5 w-3.5" />
            What a brief includes
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {["Company Snapshot", "4 Competitors", "2×2 Positioning Map", "Feature Gap Analysis", "3 Opportunities", "PM Next Steps"].map((t) => (
              <div
                key={t}
                className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-navy"
              >
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
